import { useEffect, useState } from 'react';

import { PlanPhaseTypeEnum, TradingPlan } from '../../../lib/models/crm/types';
import MarketStateCache from '../../../lib/store-util/MarketStateCache';
import { isMarketRegularHours } from '../../../lib/util/MarketDataHelpers';
import { NullableNumber } from '../../../lib/util/types';

import { calculatePotentialPayout } from '../payoutsHelpers';

type UseWithdrawButtonParams = {
  currentPlan?: TradingPlan;
  isGettingFirstAndLastTrade: boolean;
  availableCash: number[];
  payoutSplit: NullableNumber;
  totalRealizedPnl: number | undefined;
  hasOlderTradeThanFourteenDays: boolean;
};

type UseWithdrawButtonReturn = {
  isWithdrawButtonEnabled: boolean;
  potentialPayout: number;
};

/**
 * @param params - Hook parameters including plan data, loading states, and trade history
 * @returns Object containing button disabled state and calculated potential payout
 */
export const useWithdrawButton = ({
  currentPlan,
  isGettingFirstAndLastTrade,
  availableCash,
  payoutSplit,
  totalRealizedPnl,
  hasOlderTradeThanFourteenDays,
}: UseWithdrawButtonParams): UseWithdrawButtonReturn => {
  const { marketState } = MarketStateCache.use();
  const isMarketHours = isMarketRegularHours(marketState);
  const [cashSum, setCashSum] = useState<number>(0);
  const [potentialPayout, setPotentialPayout] = useState<number>(0);

  // Calculate sum of available cash from all accounts
  useEffect(() => {
    const sum = availableCash.reduce((acc, num) => acc + num, 0);
    setCashSum(sum);
  }, [availableCash]);

  // Calculate potential payout based on payout split, cash sum, and realized PnL
  useEffect(() => {
    if (typeof payoutSplit === 'number') {
      setPotentialPayout(
        calculatePotentialPayout(
          payoutSplit,
          cashSum,
          totalRealizedPnl,
          currentPlan?.penalty_payout_split ?? undefined,
          currentPlan?.consistency_coefficient ?? undefined,
        ),
      );
    }
  }, [payoutSplit, cashSum, totalRealizedPnl, currentPlan]);

  const isChallengePhase = currentPlan?.phase === PlanPhaseTypeEnum.challenge;

  const isWithdrawButtonEnabled = !isGettingFirstAndLastTrade
  && hasOlderTradeThanFourteenDays
  && potentialPayout > 0
  && !isMarketHours
  && !isChallengePhase;

  return {
    isWithdrawButtonEnabled,
    potentialPayout,
  };
};
